/// <reference types="vite/client" />
/**
 * /audits/<engagement> — per-audit drill-down (#18).
 *
 * Renders the audit state-machine timeline (swarm_actions for the
 * engagement), the report markdown, the sent-email outcome + errors, and
 * operator actions (Resend, Mark email blocked).
 *
 * No per-audit LLM-call panel: Analytics Engine records a constant
 * company + coarse task with no engagement slot, so there is no truthful
 * way to attribute LLM calls to a single audit.
 */

import { useCallback, useEffect, useState } from "react";
import { ErrorBanner } from "../components/ErrorBanner";
import { renderMarkdown } from "../utils/render-markdown";

interface TimelineRow {
  id: string;
  timestamp: string;
  agent: string;
  action: string;
  status: string;
  engagementId: string;
  details: Record<string, unknown>;
}

interface AuditDetailData {
  engagement: {
    id: string;
    name: string | null;
    status: string;
    kind: string;
    ownerEmail: string | null;
    identity: Record<string, unknown> | null;
  } | null;
  report: { key: string; markdown: string } | null;
  timeline: TimelineRow[];
  lead: {
    id: string;
    status: string;
    source: string;
    email: string | null;
    companyName: string;
  } | null;
  emailEvents: Array<{
    id: string;
    createdAt: string;
    eventType: string;
    url: string | null;
    provenance: string;
  }>;
}

/** Read the engagement id from `/audits/<id>` (decoded). */
function engagementIdFromPath(): string | null {
  const m = window.location.pathname.match(/^\/audits\/(.+)$/);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]).replace(/\/+$/, "");
  } catch {
    return m[1];
  }
}

function formatTs(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

/** Map a swarm_actions status to a theme status token colour. */
function statusColor(status: string): string {
  if (status === "failed") return "var(--color-status-error)";
  if (status === "pending_approval") return "var(--color-status-paused)";
  return "var(--color-status-active)";
}

export function AuditDetail() {
  const engagementId = engagementIdFromPath();
  const [data, setData] = useState<AuditDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<"resend" | "block" | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!engagementId) {
        setError("No engagement id in the URL.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      fetch(`/api/audits/detail?engagement=${encodeURIComponent(engagementId)}`, { signal })
        .then(async (res) => {
          if (!res.ok) {
            const body = await res.text().catch(() => "");
            throw new Error(`HTTP ${res.status}${body ? ` — ${body.slice(0, 200)}` : ""}`);
          }
          return res.json() as Promise<AuditDetailData>;
        })
        .then((d) => {
          // Guarantee the arrays so a malformed-but-200 body can't crash the
          // timeline / email-events renders downstream.
          setData({ ...d, timeline: d.timeline ?? [], emailEvents: d.emailEvents ?? [] });
          setLoading(false);
        })
        .catch((err: Error) => {
          if (err.name === "AbortError") return;
          setError(err.message);
          setLoading(false);
        });
    },
    [engagementId],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const runAction = useCallback(
    async (kind: "resend" | "block") => {
      if (!engagementId) return;
      setActionBusy(kind);
      setActionMsg(null);
      try {
        const path = kind === "resend" ? "/api/audits/resend" : "/api/audits/mark-blocked";
        const res = await fetch(path, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ engagement: engagementId }),
        });
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        if (!res.ok) throw new Error(body.message || `HTTP ${res.status}`);
        setActionMsg(
          kind === "resend"
            ? "Relance déclenchée — l'Integrator renvoie l'email en cache."
            : "Statut EMAIL_BLOCKED enregistré.",
        );
        // Reflect the new state machine row.
        load();
      } catch (err) {
        setActionMsg(`Échec : ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setActionBusy(null);
      }
    },
    [engagementId, load],
  );

  if (error) {
    return <ErrorBanner message={error} onRetry={load} context="audit detail" />;
  }

  const emailRows = data?.timeline.filter((r) => r.action.startsWith("audit_email_")) ?? [];
  const failureRows = data?.timeline.filter((r) => r.action === "onboarding_failed") ?? [];

  return (
    <div className="space-y-6 lg:space-y-8">
      <header className="reveal">
        <a
          href="/audits"
          className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)] hover:text-[var(--color-accent)] transition-colors"
        >
          ← Tous les audits
        </a>
        <p className="eyebrow mt-3">Console · 07 · Détail</p>
        <h2 className="font-display italic text-3xl sm:text-4xl lg:text-[48px] leading-[0.95] mt-2 tracking-tight">
          <span className="text-accent-gradient">{data?.engagement?.name || engagementId}</span>
        </h2>
        {data?.engagement && (
          <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px] text-[var(--color-text-muted)]">
            <span>engagement : {data.engagement.id}</span>
            <span>statut : {data.engagement.status}</span>
            <span>type : {data.engagement.kind}</span>
            {data.engagement.ownerEmail && <span>contact : {data.engagement.ownerEmail}</span>}
          </p>
        )}
      </header>

      {loading && !data && (
        <div className="flex items-center gap-3 text-[var(--color-text-dim)]">
          <span className="live-dot text-[var(--color-accent)]" />
          <span className="font-mono text-xs tracking-[0.18em] uppercase blink">Chargement…</span>
        </div>
      )}

      {data && (
        <>
          {/* Operator actions */}
          <section className="panel p-4 sm:p-5">
            <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)] mb-3">
              Actions
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => runAction("resend")}
                disabled={actionBusy !== null}
                className="font-mono text-[10px] uppercase tracking-wider px-4 py-2 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: "var(--color-accent-soft)",
                  color: "var(--color-accent-glow)",
                  border: "1px solid var(--color-accent)",
                }}
              >
                {actionBusy === "resend" ? "…" : "Relancer l'email"}
              </button>
              <button
                type="button"
                onClick={() => runAction("block")}
                disabled={actionBusy !== null}
                className="font-mono text-[10px] uppercase tracking-wider px-4 py-2 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: "var(--color-bg-card)",
                  color: "var(--color-status-paused)",
                  border: "1px solid var(--color-status-paused)",
                }}
              >
                {actionBusy === "block" ? "…" : "Marquer email bloqué"}
              </button>
              {actionMsg && (
                <span className="font-mono text-[11px] text-[var(--color-text-muted)]">{actionMsg}</span>
              )}
            </div>
          </section>

          {/* Sent-email outcome + errors */}
          <section className="grid lg:grid-cols-2 gap-4">
            <div className="panel p-4 sm:p-5">
              <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)] mb-3">
                Email d'audit
              </p>
              {emailRows.length === 0 ? (
                <p className="text-sm text-[var(--color-text-muted)]">Aucun envoi enregistré.</p>
              ) : (
                <ul className="space-y-2">
                  {emailRows.map((r) => (
                    <li key={r.id} className="font-mono text-[11px]">
                      <span style={{ color: r.action.includes("failed") ? "var(--color-status-error)" : "var(--color-status-active)" }}>
                        {r.action.replace("audit_email_", "")}
                      </span>
                      <span className="text-[var(--color-text-dim)]"> · {formatTs(r.timestamp)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {data.emailEvents.length > 0 && (
                <div className="mt-4 pt-3 border-t border-[var(--color-border)]">
                  <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)] mb-2">
                    Engagement email ({data.emailEvents.length})
                  </p>
                  <ul className="space-y-1 font-mono text-[11px] text-[var(--color-text-muted)]">
                    {data.emailEvents.slice(0, 8).map((e) => (
                      <li key={e.id}>
                        {e.eventType} · {e.provenance} · {formatTs(e.createdAt)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="panel p-4 sm:p-5">
              <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)] mb-3">
                Lead
              </p>
              {data.lead ? (
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <dt className="text-[var(--color-text-dim)] font-mono text-[10px] uppercase">Société</dt>
                  <dd className="text-[var(--color-text)]">{data.lead.companyName}</dd>
                  <dt className="text-[var(--color-text-dim)] font-mono text-[10px] uppercase">Statut</dt>
                  <dd className="text-[var(--color-text)]">{data.lead.status}</dd>
                  <dt className="text-[var(--color-text-dim)] font-mono text-[10px] uppercase">Source</dt>
                  <dd className="text-[var(--color-text)]">{data.lead.source}</dd>
                  {data.lead.email && (
                    <>
                      <dt className="text-[var(--color-text-dim)] font-mono text-[10px] uppercase">Email</dt>
                      <dd className="text-[var(--color-text)] break-all">{data.lead.email}</dd>
                    </>
                  )}
                </dl>
              ) : (
                <p className="text-sm text-[var(--color-text-muted)]">Aucun lead lié.</p>
              )}
            </div>
          </section>

          {failureRows.length > 0 && (
            <section className="panel p-4 sm:p-5" style={{ borderColor: "var(--color-status-error)" }}>
              <p className="font-mono text-[10px] tracking-[0.18em] uppercase mb-3" style={{ color: "var(--color-status-error)" }}>
                Échecs
              </p>
              <ul className="space-y-2">
                {failureRows.map((r) => (
                  <li key={r.id} className="font-mono text-[11px]">
                    <span className="text-[var(--color-text-dim)]">{formatTs(r.timestamp)} · </span>
                    <span className="text-[var(--color-text)]">
                      {String((r.details as { reason?: unknown }).reason ?? "raison inconnue")}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* State-machine timeline */}
          <section className="panel p-4 sm:p-5">
            <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)] mb-3">
              Timeline ({data.timeline.length})
            </p>
            {data.timeline.length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)]">Aucune action enregistrée.</p>
            ) : (
              <ul className="divide-y divide-[var(--color-border)]">
                {data.timeline.map((r) => {
                  const expanded = expandedId === r.id;
                  return (
                    <li key={r.id} className="py-2">
                      <button
                        onClick={() => setExpandedId(expanded ? null : r.id)}
                        className="w-full flex items-center gap-3 text-left font-mono text-[11px]"
                        aria-expanded={expanded}
                      >
                        <span className="text-[var(--color-text-dim)] tabular-nums w-36 shrink-0">
                          {formatTs(r.timestamp)}
                        </span>
                        <span className="text-[var(--color-text-muted)] flex-1">{r.action}</span>
                        <span style={{ color: statusColor(r.status) }}>{r.status}</span>
                      </button>
                      {expanded && Object.keys(r.details).length > 0 && (
                        <pre className="mt-2 ml-36 text-[10px] font-mono text-[var(--color-text-muted)] bg-[var(--color-bg-card)] p-3 rounded overflow-x-auto">
                          {JSON.stringify(r.details, null, 2)}
                        </pre>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Report markdown */}
          <section className="panel p-4 sm:p-6">
            <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)] mb-3">
              Rapport d'audit
            </p>
            {data.report ? (
              <div>{renderMarkdown(data.report.markdown)}</div>
            ) : (
              <p className="text-sm text-[var(--color-text-muted)]">
                Aucun rapport en R2 pour cet engagement.
              </p>
            )}
          </section>
        </>
      )}
    </div>
  );
}
